import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { isAxiosError } from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { useRegisterTerminal, useTerminalProfiles } from '@/hooks/use-terminals';
import { useLocations } from '@/hooks/use-locations';
import { useT } from '@/i18n/use-t';
import { ActivationCodeAlert } from './terminals-list';

const Schema = z
  .object({
    code: z
      .string()
      .min(1)
      .max(64)
      .regex(/^[A-Z0-9-]+$/, 'uppercase alphanumeric + dash, e.g. POS-01'),
    displayName: z.string().min(1).max(255),
    kind: z.enum(['pos', 'sco', 'attendant', 'link']),
    profileId: z.string().optional(),
    locationId: z.string().optional(),
    // VL-266. Empty string is the untouched form field, not a value — it is
    // mapped to `undefined` on submit rather than sent as ''.
    posExternalNumber: z
      .string()
      .regex(/^[0-9]{3,5}$/, '3 to 5 digits, e.g. 6001')
      .or(z.literal(''))
      .optional(),
  })
  // RL-449: a Link device serves exactly one store, so its location is
  // required; the server refuses it too, this only says so before submitting.
  .refine((values) => values.kind !== 'link' || (values.locationId ?? '') !== '', {
    path: ['locationId'],
    message: 'A Link device must be assigned to a location',
  });
type FormValues = z.infer<typeof Schema>;

export function TerminalNewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Reached from Terminal Management → Link devices with `?kind=link`.
  const presetLink = searchParams.get('kind') === 'link';
  const register = useRegisterTerminal();
  const profiles = useTerminalProfiles();
  const locations = useLocations({ status: ['active'] });
  const [code, setCode] = useState<{ activationCode: string; expiresAt: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const t = useT('admin_ui');

  const {
    register: rhf,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(Schema),
    defaultValues: {
      code: '',
      displayName: '',
      kind: presetLink ? 'link' : 'pos',
      posExternalNumber: '',
    },
  });
  // A Link has no terminal profile and no register number; those fields are
  // hidden for it rather than offered and then refused by the server.
  const isLink = watch('kind') === 'link';
  const backTo = isLink ? '/terminals/link' : '/terminals';

  return (
    <div className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">
            {t('terminals.registerTerminal', 'Register terminal')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              'terminals.new.help',
              'The activation code is shown once on success — read it to the operator on-site.',
            )}
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to={backTo}>{t('terminals.new.back', 'Back')}</Link>
        </Button>
      </header>

      {code !== null && (
        <ActivationCodeAlert
          activationCode={code.activationCode}
          expiresAt={code.expiresAt}
          onClose={() => navigate(backTo)}
        />
      )}

      {code === null && (
        <form
          className="grid max-w-2xl gap-4 rounded-md border bg-card p-5"
          onSubmit={handleSubmit(async (values) => {
            setError(null);
            try {
              const link = values.kind === 'link';
              const res = await register.mutateAsync({
                ...values,
                profileId: link || values.profileId === '' ? undefined : values.profileId,
                locationId: values.locationId === '' ? undefined : values.locationId,
                posExternalNumber:
                  link || values.posExternalNumber === '' ? undefined : values.posExternalNumber,
              });
              setCode({ activationCode: res.activationCode, expiresAt: res.expiresAt });
            } catch (err) {
              setError(extractError(err, t));
            }
          })}
        >
          <div className="space-y-1.5">
            <Label>{t('terminals.field.code', 'Code')}</Label>
            <Input className="font-mono" placeholder="POS-01" {...rhf('code')} />
            {errors.code?.message !== undefined && (
              <p className="text-xs text-destructive">{errors.code.message}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>{t('terminals.field.displayName', 'Display name')}</Label>
            <Input
              placeholder={t('terminals.new.displayNamePlaceholder', 'Front-counter POS')}
              {...rhf('displayName')}
            />
            {errors.displayName?.message !== undefined && (
              <p className="text-xs text-destructive">{errors.displayName.message}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>{t('terminals.field.kind', 'Kind')}</Label>
            <Select {...rhf('kind')}>
              <option value="pos">pos</option>
              <option value="sco">sco</option>
              <option value="attendant">attendant</option>
              <option value="link">{t('terminals.link.kindOption', 'Link device')}</option>
            </Select>
            {isLink && (
              <p className="text-xs text-muted-foreground">
                {t(
                  'terminals.link.kindHelp',
                  'An in-store connector host. It serves the connector sources of the location below and never sells.',
                )}
              </p>
            )}
          </div>
          <div className={isLink ? 'hidden' : 'space-y-1.5'}>
            <Label>{t('terminals.new.profileOptional', 'Profile (optional)')}</Label>
            <Select {...rhf('profileId')}>
              <option value="">{t('terminals.new.none', '— none —')}</option>
              {profiles.data?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.primaryLocale})
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>
              {isLink
                ? t('terminals.link.locationRequired', 'Location')
                : t('terminals.new.locationOptional', 'Location (optional)')}
            </Label>
            <Select {...rhf('locationId')}>
              <option value="">{t('terminals.unassigned', '— unassigned —')}</option>
              {locations.data?.items.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.code} — {location.name}
                </option>
              ))}
            </Select>
            {errors.locationId?.message !== undefined && (
              <p className="text-xs text-destructive">
                {t(
                  'terminals.link.locationMissing',
                  'A Link device must be assigned to a location',
                )}
              </p>
            )}
          </div>
          {/* VL-266 — optional here on purpose: a register is commonly stood
              up before anyone knows the number the back office files it under,
              and the column on the terminals list edits it afterwards. */}
          <div className={isLink ? 'hidden' : 'space-y-1.5'}>
            <Label>
              {t('terminal.posExternalNumber.optional', 'POS external number (optional)')}
            </Label>
            <Input placeholder="6001" inputMode="numeric" {...rhf('posExternalNumber')} />
            <p className="text-xs text-muted-foreground">
              {t(
                'terminal.posExternalNumber.hint',
                "The register's own number in the back office — 3 to 5 digits. Leave it empty if the register has none.",
              )}
            </p>
            {errors.posExternalNumber?.message !== undefined && (
              <p className="text-xs text-destructive">{errors.posExternalNumber.message}</p>
            )}
          </div>
          {error !== null && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end">
            <Button type="submit" disabled={register.isPending}>
              {register.isPending
                ? t('terminals.new.submitting', 'Registering…')
                : t('terminals.new.submit', 'Register')}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

function extractError(err: unknown, t: (key: string, bakedEnglish?: string) => string): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as { message?: string } | undefined;
    return data?.message ?? err.message;
  }
  return t('terminals.error.unexpected', 'Unexpected error');
}
